import React from "react"
import toast from "react-hot-toast"
import { useApp } from "../context/AppContext"
import { Icon, useT, useIsMobile } from "../shared"
import { Stat, ProgramBanner, TableWrap, Btn } from "../shared/components"
import { listCourses } from "../api/courses.api"
import {
  programAnalytics,
  programProgress,
  downloadProgressCsv,
  downloadImoCsv,
} from "../api/users.api"

/* ============================================================
   Program Detail — FR-10.4
   Opened when an admin clicks a program card on the dashboard.
   Reads the active program from AppContext (set by the caller).
   ============================================================ */
export default function ProgramDetailScreen() {
  const t = useT()
  const { lang, navigate, currentProgram, currentProgramId } = useApp()
  const isMobile = useIsMobile()
  const [analytics, setAnalytics] = React.useState(null)
  const [courses, setCourses] = React.useState([])
  const [participants, setParticipants] = React.useState([])
  const [exporting, setExporting] = React.useState(null)

  React.useEffect(() => {
    if (!currentProgramId) return
    programAnalytics(currentProgramId).then(setAnalytics).catch(() => setAnalytics(null))
    listCourses(currentProgramId).then(setCourses).catch(() => setCourses([]))
    programProgress(currentProgramId).then(setParticipants).catch(() => setParticipants([]))
  }, [currentProgramId])

  const handleExport = async (kind) => {
    setExporting(kind)
    try {
      const blob = kind === "imo"
        ? await downloadImoCsv(currentProgramId)
        : await downloadProgressCsv(currentProgramId)
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = `${kind}-${currentProgram?.code || "program"}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message)
    } finally {
      setExporting(null)
    }
  }

  if (!currentProgramId) {
    return (
      <div className="main-inner">
        <p className="muted">{lang === "id" ? "Program tidak dipilih." : "No program selected."}</p>
      </div>
    )
  }

  return (
    <div className="main-inner">
      <div className="page-header">
        <div className="page-header-text">
          <div className="eyebrow">
            <Btn variant="ghost" size="sm" icon={<Icon name="chevron_left" size={12}/>}
              onClick={() => navigate("dashboard")}
              style={{ marginRight: 4, padding: "2px 8px", height: "auto" }}>
              {lang === "id" ? "Dashboard" : "Dashboard"}
            </Btn>
          </div>
          <h1 className="h1" style={{ marginTop: 6 }}>{currentProgram?.name || "Program"}</h1>
        </div>
        <div className="page-header-actions">
          <Btn variant="secondary" icon={<Icon name="download" size={14} />}
            onClick={() => handleExport("progress")} disabled={exporting != null}>
            {exporting === "progress"
              ? (lang === "id" ? "Mengunduh..." : "Downloading...")
              : (lang === "id" ? "Ekspor Progres" : "Export Progress")}
          </Btn>
          <Btn variant="primary" icon={<Icon name="download" size={14} />}
            onClick={() => handleExport("imo")} disabled={exporting != null}>
            {exporting === "imo"
              ? (lang === "id" ? "Mengunduh..." : "Downloading...")
              : (lang === "id" ? "Ekspor Laporan IMO" : "Export IMO Report")}
          </Btn>
        </div>
      </div>

      <ProgramBanner program={currentProgram} lang={lang} />

      {/* Kode pendaftaran mandiri pindah ke Pelatihan → tab Kelas: kodenya
          sekarang menempel di angkatan, bukan di program. */}

      {/* Summary */}
      <div className="grid grid-4 mb-5">
        <Stat
          label={lang === "id" ? "Total peserta" : "Total participants"}
          value={String(analytics?.total ?? 0)}
        />
        <Stat
          label={lang === "id" ? "Lulus (sertifikat)" : "Certified"}
          value={String(analytics?.certified ?? 0)}
        />
        <Stat
          label={lang === "id" ? "Pending sertifikat" : "Cert pending"}
          value={String(analytics?.certPending ?? 0)}
        />
        <Stat
          label={lang === "id" ? "Rata-rata Learning Gain" : "Avg Learning Gain"}
          value={analytics?.avgGain != null ? `+${Math.round(analytics.avgGain)}` : "—"}
          hint={lang === "id" ? "target ≥ 20" : "target ≥ 20"}
        />
      </div>

      <div className="grid" style={{ gridTemplateColumns: isMobile ? "1fr" : "1fr 2fr", gap: 16 }}>
        {/* Courses */}
        <div className="tile">
          <div className="tile-header">
            <h3>{lang === "id" ? "Kursus" : "Courses"}</h3>
            <span className="tile-header-sub">{courses.length}</span>
          </div>
          <div>
            {courses.length === 0 ? (
              <div className="tile-pad muted small" style={{ textAlign: "center" }}>
                {lang === "id" ? "Belum ada kursus." : "No courses."}
              </div>
            ) : (
              courses.map((c, i) => {
                // FR-22: quota display
                const hasQuota = c.quota != null
                const enrolled = c.enrolledCount ?? 0
                const isFull = hasQuota && enrolled >= c.quota
                const pct = hasQuota ? Math.min(100, Math.round((enrolled / c.quota) * 100)) : 0

                return (
                  <div
                    key={c.id}
                    className="col gap-1"
                    style={{ padding: "12px 16px", borderTop: i > 0 ? "1px solid var(--border)" : "none" }}
                  >
                    <div className="row gap-2">
                      <Icon name="book" size={14} color="var(--text-tertiary)" />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="small" style={{ fontWeight: 500 }}>{c.title}</div>
                        <div className="tiny muted">{c._count?.modules ?? 0} {lang === "id" ? "modul" : "modules"}</div>
                      </div>
                      <div className="row gap-1" style={{ flexShrink: 0 }}>
                        {isFull && (
                          <span className="tag tiny" style={{ background: "var(--red-soft, #fee2e2)", color: "var(--red-ink, #991b1b)" }}>
                            {lang === "id" ? "Penuh" : "Full"}
                          </span>
                        )}
                        <span className={"tag tiny " + (c.status === "PUBLISHED" ? "green" : "outline")}>
                          {c.status || "DRAFT"}
                        </span>
                      </div>
                    </div>
                    {/* FR-22: quota progress bar */}
                    {hasQuota && (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4, paddingLeft: 22 }}>
                        <div style={{ flex: 1, height: 4, background: "var(--border)", borderRadius: 2, overflow: "hidden" }}>
                          <div style={{
                            height: "100%",
                            width: pct + "%",
                            background: isFull ? "var(--red-ink, #dc2626)" : "var(--program-color)",
                            borderRadius: 2,
                            transition: "width 0.3s",
                          }} />
                        </div>
                        <span className="mono tiny muted" style={{ flexShrink: 0 }}>
                          {enrolled}/{c.quota}
                        </span>
                      </div>
                    )}
                  </div>
                )
              })
            )}
          </div>
        </div>

        {/* Participants */}
        <div className="tile">
          <div className="tile-header">
            <h3>{lang === "id" ? "Peserta" : "Participants"}</h3>
            <span className="tile-header-sub">{participants.length}</span>
          </div>
          {participants.length === 0 ? (
            <div className="tile-pad muted small" style={{ textAlign: "center" }}>
              {lang === "id" ? "Belum ada peserta." : "No participants."}
            </div>
          ) : (
            <TableWrap>
            <table className="table">
              <thead><tr>
                <th>{lang === "id" ? "Nama" : "Name"}</th>
                <th>Pretest</th>
                <th>Posttest</th>
                <th>{lang === "id" ? "Kehadiran" : "Attendance"}</th>
                <th>{lang === "id" ? "Sertifikat" : "Cert"}</th>
              </tr></thead>
              <tbody>
                {participants.map((p) => (
                  <tr key={p.userId}>
                    <td>
                      <div className="small" style={{ fontWeight: 500 }}>{p.name}</div>
                      <div className="tiny muted">{p.email}</div>
                    </td>
                    <td className="mono small tnum">{p.pretest != null ? Math.round(p.pretest) : "—"}</td>
                    <td className="mono small tnum">{p.posttest != null ? Math.round(p.posttest) : "—"}</td>
                    <td className="mono small tnum">{Math.round(p.attendance ?? 0)}%</td>
                    <td>
                      {p.certEligible
                        ? <span className="tag green tiny"><span className="dot" />{lang === "id" ? "Eligible" : "Eligible"}</span>
                        : <span className="tag outline tiny">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </TableWrap>
          )}
        </div>
      </div>
    </div>
  )
}
